import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Wine, Music, Award, Camera, Utensils, Users, Star } from 'lucide-react';

const Event = () => {
  return (
    <div className="relative">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
        </motion.div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6">
              The Most Prestigious
              <span className="block" style={{ color: '#C1B3A3' }}>
                FinTech Event of 2025
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8">
              An Unforgettable Evening of Excellence, Innovation, and Celebration
            </p>
          </motion.div>
        </div>
      </section>

      {/* Event Highlights */}
      <section className="relative py-20 overflow-hidden" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1505236858219-8359eb29e329?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#C1B3A3' }}>
              A Night to Remember
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Join 500+ industry leaders, innovators, and visionaries for an evening of recognition, networking, and celebration
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Calendar className="h-8 w-8" />,
                title: "25th November, 2025",
                description: "Mark your calendar for the most anticipated fintech event of the year"
              },
              {
                icon: <MapPin className="h-8 w-8" />,
                title: "Exclusive Location",
                description: "Our prestigious venue in Pretoria will be revealed later this year"
              },
              {
                icon: <Clock className="h-8 w-8" />,
                title: "18:00 - Late",
                description: "An evening of celebration, networking, and entertainment"
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-all duration-300"
                style={{ backgroundColor: 'rgba(193, 179, 163, 0.1)' }}
              >
                <div className="mb-4 flex justify-center" style={{ color: '#C1B3A3' }}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p style={{ color: '#C1B3A3' }}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="text-4xl font-bold" style={{ color: '#1A1A1A' }}>
                An Evening of Excellence
              </h2>
              <div className="space-y-6">
                {[
                  {
                    icon: <Wine className="h-6 w-6" />,
                    title: "Welcome Reception",
                    description: "Start your evening with premium cocktails and canapés"
                  },
                  {
                    icon: <Utensils className="h-6 w-6" />,
                    title: "Gala Dinner",
                    description: "Indulge in a carefully curated three-course meal with wine pairing"
                  },
                  {
                    icon: <Award className="h-6 w-6" />,
                    title: "Awards Ceremony",
                    description: "Witness the recognition of excellence in 12 prestigious categories"
                  },
                  {
                    icon: <Music className="h-6 w-6" />,
                    title: "Entertainment",
                    description: "Live performances and music throughout the evening"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-4"
                  >
                    <div style={{ color: '#C1B3A3' }}>{item.icon}</div>
                    <div>
                      <h3 className="font-semibold mb-1">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1515169067868-5387ec356754?auto=format&fit=crop&q=80"
                  alt="Gala Event"
                  className="object-cover w-full h-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Attend Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(193, 179, 163, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#1A1A1A' }}>
              Why You Can't Miss This
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The SA FinTech Awards is more than just an awards ceremony - it's the premier gathering of fintech leaders and innovators
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Users className="h-12 w-12" />,
                title: "Elite Networking",
                description: "Connect with 500+ industry leaders, investors, and innovators"
              },
              {
                icon: <Camera className="h-12 w-12" />,
                title: "Media Coverage",
                description: "Extensive coverage from leading financial and technology media"
              },
              {
                icon: <Star className="h-12 w-12" />,
                title: "Exclusive Pretoria Venue",
                description: "A closely guarded secret location that will set new standards for luxury and sophistication"
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="mb-6" style={{ color: '#C1B3A3' }}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Event;